import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Единый форматтер числовых значений (HEAL-10). Единственная реализация
// округления на фронте — все остальные форматтеры делегируют сюда.
//
// Правила: максимум 2 знака после запятой, trailing zeros режем, никаких
// float-хвостов вроде -0.42999999999999997 / -1.7000000000000001.
// 4.5700 → "4.57", 10.00 → "10", 5.50 → "5.5", -0.42999999999999997 → "-0.43".
//
// Принимает number и string (API отдаёт numeric-колонки строками),
// а также null/undefined → "—". Нечисловой ввод возвращаем как есть,
// чтобы не падать на качественных значениях ("положительно").
export function formatNumber(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '—'
  const num = typeof n === 'number' ? n : Number(n)
  if (!Number.isFinite(num)) return String(n)
  return num.toFixed(2).replace(/\.?0+$/, '')
}

// Процент динамики: тот же формат, что и значения (2 знака, без хвостов).
// 33.3333% → "33.33", 5.00% → "5", 0.567% → "0.57", -8.5999...% → "-8.6".
export function formatPct(n: number): string {
  return formatNumber(n)
}

// Абсолютное изменение биомаркера. Делегирует в единый форматтер.
export function formatDeltaValue(n: number): string {
  return formatNumber(n)
}

// Заглавная первая буква имени биомаркера (каталог врача в непоследовательном
// регистре: «глюкоза», «общий белок»). Меняем только если первый символ —
// строчная кириллица/латиница; цифры/аббревиатуры/символы/уже-заглавные не трогаем
// («17-ОН», «B12», «ИФР-1», «α-амилаза», «ТТГ»). «pH» — намеренный регистр, сохраняем.
export function capitalizeFirst(s: string): string {
  if (!s) return s
  if (/^p[Hh]/.test(s)) return s
  const first = s.charAt(0)
  if (!/[a-zа-яё]/.test(first)) return s
  return first.toUpperCase() + s.slice(1)
}

/** Русская плюрализация: forms = [1, 2-4, 5+]. Напр. ['анализ','анализа','анализов']. */
export function pluralize(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return forms[0]
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1]
  return forms[2]
}
